import { LogOut, PlusIcon, Settings } from 'lucide-react';
import { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../../hooks/use-user';
import { UserRole, UserType } from '../../lib/interfaces/user';
import AddIcon from '../../ui/components/icons/add-icon';
import DashboardIcon from '../../ui/components/icons/dashboard-icon';
import FingerPrintIcon from '../../ui/components/icons/finger-print-icon';
import LearningSuiteIcon from '../../ui/components/icons/learning-suite-icon';
import NotificationsIcon from '../../ui/components/icons/notifications-icon';
import OrdersIcon from '../../ui/components/icons/orders-icon';
import SupportIcon from '../../ui/components/icons/support-icon';
import TemplatesIcon from '../../ui/components/icons/templates-icon';
import UserIcon from '../../ui/components/icons/user-icon';
import MyAvatar from '../../ui/components/ui/my-avatar';
import NewOrder from '../../ui/components/ui/new-order';
import MessagesPopup from '../../ui/components/ui/notification-popup';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/components/ui/tooltip';
import { Context } from '../root/context';

interface SidebarItemI {
  title: string;
  icon: JSX.Element;
  link: string;
  element?: React.ReactNode;
  end?: boolean;
  isActive?: boolean;
  tooltipTitle?: string;
  submenu?: SubMenuItemI[];
  showSubmenu?: boolean;
}

interface SubMenuItemI {
  title: string;
  link: string;
  show?: boolean;
}

export enum MessageStatusEnum {
  TEXT = 'Text',
  ERROR = 'Error',
  ROCKET = 'Rocket'
}

export interface IMessage {
  _id: string;
  picture: string;
  opened: boolean;
  title: string;
  text: string;
  date: string;
  number: string;
  status: MessageStatusEnum;
}

const sidebarCreateOrder = {
  title: 'Neuer Auftrag',
  icon: <AddIcon />,
  element: (
    <NewOrder
      trigger={
        <div className="w-full h-full flex justify-center items-center">
          <AddIcon />
        </div>
      }
    />
  ),
  link: '/order/create',
  tooltipTitle: 'Neuer Auftrag'
};

const sidebarOrders = {
  title: 'Auftrag ansehen',
  icon: <OrdersIcon />,
  link: '/order',
  tooltipTitle: 'Aufträge'
};

const sidebarUsers = {
  title: 'Benutzer',
  icon: <UserIcon />,
  link: '/collaborators',
  tooltipTitle: 'Benutzer',
  showSubmenu: false,
  submenu: [
    {
      title: 'Benutzer',
      link: '/collaborators'
    },
    {
      title: 'Einladungen',
      link: '/collaborators/invitations'
    }
  ]
};

const sidebarNotifications = {
  title: '',
  icon: <NotificationsIcon />,
  link: '/notifications',
  tooltipTitle: 'Benachrichtigungen'
};

/*const sidebar7i7BestPractices = {
  title: '7i7 Leitfaden',
  icon: <LightningIcon />,
  link: '/7i7-best-practices',
  tooltipTitle: '7i7 Leitfaden'
};*/

const sidebarSettings = {
  title: 'Settings',
  icon: <Settings />,
  link: '/settings',
  end: true,
  tooltipTitle: 'Einstellungen'
};

const sidebarLogout = {
  title: 'Hilfe',
  icon: <LogOut />,
  link: '/logout',
  end: true,
  tooltipTitle: 'Ausloggen'
};

const sidebarSupport = {
  title: 'Support',
  icon: <SupportIcon />,
  link: '/support',
  end: true,
  tooltipTitle: 'Support'
};

const sidebarOrganisations = {
  title: 'Organisations',
  icon: <FingerPrintIcon />,
  link: '/organisations',
  tooltipTitle: 'Kunden'
};

const sidebarOrganisationsAdmin = {
  ...sidebarOrganisations,
  showSubmenu: true,
  submenu: [
    {
      title: 'Kunden',
      link: '/organisations',
      show: true
    },
    {
      title: 'Produktmanagement',
      link: '/products',
      show: true
    },
    {
      title: 'Angebote',
      link: '/offers',
      show: true
    }
  ]
};

const sidebarAdminTeamleaderDashboardSubmenu = {
  title: 'Dashboard',
  link: '/dashboard',
  show: true
};

const sidebarAdminTeamleaderBonusSubmenu = {
  title: 'Bonus',
  link: '/bonus',
  show: true
};

const sidebarAdminTeamleaderEmailsSubmenu = {
  title: 'E-Mails',
  link: '/emails',
  show: true
};

const bannerAdminSubmenu = {
  title: 'Banner',
  link: '/banner',
  show: true
};

const kpiDashboardAdminSubmenu = {
  title: "KPI's",
  link: '/kpi',
  show: true
};

const sidebarLearningSuite = {
  title: 'Learning Suite',
  icon: <LearningSuiteIcon />,
  link: '/learning-suite',
  tooltipTitle: 'Learning Suite'
};

const sidebarLearningSuiteAdmin = {
  title: 'Learning Suite Admin',
  icon: <LearningSuiteIcon />,
  link: '/learning-suite',
  tooltipTitle: 'Learning Suite Admin',
  showSubmenu: true,
  submenu: [
    {
      title: 'Learning Suite',
      link: '/learning-suite',
      show: true
    },
    {
      title: 'Verwaltung',
      link: '/learning-suite/admin',
      show: true
    }
  ]
};

const sidebarTeamleaderMenu = {
  title: 'Dashboard',
  icon: <DashboardIcon />,
  link: '/dashboard',
  tooltipTitle: 'Dashboard',
  showSubmenu: true,
  submenu: [sidebarAdminTeamleaderDashboardSubmenu, sidebarAdminTeamleaderBonusSubmenu]
};

const sidebarAdminMenu = {
  title: 'Dashboard',
  icon: <DashboardIcon />,
  link: '/dashboard',
  tooltipTitle: 'Dashboard',
  showSubmenu: true,
  submenu: [
    sidebarAdminTeamleaderDashboardSubmenu,
    sidebarAdminTeamleaderBonusSubmenu,
    kpiDashboardAdminSubmenu,
    sidebarAdminTeamleaderEmailsSubmenu,
    bannerAdminSubmenu
  ]
};

const sidebarBonusDashboard = {
  title: 'Bonus Dashboard',
  icon: <DashboardIcon />,
  link: '/bonus',
  tooltipTitle: 'Bonus Dashboard'
};

const sidebarTemplates = {
  title: 'Vorlagen',
  icon: <TemplatesIcon />,
  link: '/templates',
  tooltipTitle: 'Vorlagen'
};

const Sidebar = () => {
  const { user } = useUser();
  const { isMobile } = useContext(Context);

  let sidebarItems: SidebarItemI[] = [];

  if (user) {
    if (user.userType === UserType.Internal) {
      if (user.userRole === UserRole.Admin || user.userRole === UserRole.SuperAdmin) {
        sidebarItems.push(sidebarOrders);
        sidebarItems.push(sidebarTemplates);
        sidebarItems.push(sidebarNotifications);
        sidebarItems.push(sidebarUsers);
        sidebarItems.push(sidebarOrganisationsAdmin);
        sidebarItems.push(sidebarAdminMenu);
        sidebarItems.push(sidebarLearningSuiteAdmin);

        // end
        sidebarItems.push(sidebarSettings);
        sidebarItems.push(sidebarLogout);
      } else {
        if (user.isTeamLeader === false) {
          sidebarItems.push(sidebarBonusDashboard);
        }
        sidebarItems.push(sidebarOrders);
        sidebarItems.push(sidebarNotifications);
        sidebarItems.push(sidebarOrganisations);
        if (user.isTeamLeader && user.isTeamLeader === true) {
          sidebarItems.push(sidebarTeamleaderMenu);
        }
        sidebarItems.push(sidebarLearningSuite);

        // end
        sidebarItems.push(sidebarSettings);
        sidebarItems.push(sidebarLogout);
      }
    } else {
      sidebarItems.push(sidebarCreateOrder);
      sidebarItems.push(sidebarOrders);
      sidebarItems.push(sidebarTemplates);
      sidebarItems.push(sidebarNotifications);
      if (user.userRole === UserRole.Admin) {
        sidebarItems.push(sidebarUsers);
      }
      // end
      sidebarItems.push(sidebarSupport);
      sidebarItems.push(sidebarSettings);
      sidebarItems.push(sidebarLogout);
    }
  }

  const location = useLocation();

  return (
    <>
      <TooltipProvider>
        {isMobile ? (
          <div className="bg-black p-2 flex items-center flex-col">
            <Link to="/">
              <img src="../logo.svg" alt="7i7 logo" width={60} height={60} />
            </Link>
            <Link to="/order/create">
              <div className="fixed bottom-6 rounded-full right-6 bg-black p-4 z-50 drop-shadow-2xl">
                <PlusIcon size={24} className="text-white" />
              </div>
            </Link>
          </div>
        ) : (
          <div
            className="flex flex-col fixed min-h-[710px] z-20 items-center text-white w-28 h-[calc(100vh-40px)] my-5 rounded-r-[40px] pt-11 pb-9"
            style={{
              background: `
              radial-gradient(circle at 10% 10%, rgba(95, 213, 222, 0.2) 0%, transparent 40%),
              radial-gradient(circle at 90% 90%, rgba(192, 222, 96, 0.2) 0%, transparent 40%),
              black
            `
            }}>
            <div className="w-[54px] h-[58px] relative flex justify-center items-center">
              <Link to="/">
                <img src="../logo.svg" alt="7i7 logo" width={50} height={50} />
              </Link>
            </div>

            <nav className="flex flex-col gap-1 mt-10">
              {sidebarItems
                .filter((item) => !item.end)
                .map((item, index) =>
                  item.link === '/notifications' ? (
                    <MessagesPopup key={item.link} />
                  ) : (
                    <SidebarItem key={item.link} {...item} isActive={location.pathname === item.link} />
                  )
                )}
            </nav>
            <nav className="flex flex-col gap-1 mt-auto mb-3">
              {sidebarItems
                .filter((item) => item.end)
                .map((item) => (
                  <SidebarItem key={item.link} {...item} isActive={location.pathname === item.link} />
                ))}
            </nav>
            <MyAvatar />
          </div>
        )}
      </TooltipProvider>
    </>
  );
};

const SidebarItem = ({ icon, link, isActive, element, title, tooltipTitle, submenu, showSubmenu }: SidebarItemI) => {
  const [isHovered, setIsHovered] = useState(false);
  const [enterTimeout, setEnterTimeout] = useState<NodeJS.Timeout | null>(null);
  const [leaveTimeout, setLeaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();

  const handleMouseEnter = () => {
    if (!submenu || !showSubmenu) return;

    // Clear any pending leave timeout
    if (leaveTimeout) {
      clearTimeout(leaveTimeout);
      setLeaveTimeout(null);
    }

    // Set enter timeout if not already hovered
    if (!isHovered) {
      const timeout = setTimeout(() => setIsHovered(true), 100);
      setEnterTimeout(timeout);
    }
  };

  const handleMouseLeave = () => {
    if (!submenu || !showSubmenu) return;

    // Clear any pending enter timeout
    if (enterTimeout) {
      clearTimeout(enterTimeout);
      setEnterTimeout(null);
    }

    // Set leave timeout with 0.1s delay
    const timeout = setTimeout(() => setIsHovered(false), 100);
    setLeaveTimeout(timeout);
  };

  const isSubmenuActive = submenu?.some((item) => location.pathname === item.link) ?? false;
  const shouldHighlight = isActive || isSubmenuActive;

  const hasActiveSubmenu = submenu && showSubmenu;

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="relative">
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {element ? (
            <div
              className={`cursor-pointer rounded-full w-[50px] h-[50px] flex justify-center items-center bg-white hover:bg-opacity-10 ${
                shouldHighlight ? 'bg-opacity-15' : 'bg-opacity-0'
              }`}>
              {element}
            </div>
          ) : (
            <Link to={link}>
              <div
                className={`rounded-full w-[50px] h-[50px] flex justify-center items-center bg-white hover:bg-opacity-10 ${
                  shouldHighlight ? 'bg-opacity-15' : 'bg-opacity-0'
                }`}>
                {icon}
              </div>
            </Link>
          )}
        </TooltipTrigger>
        {!hasActiveSubmenu && (
          <TooltipContent side="right" className="rounded-lg">
            {tooltipTitle}
          </TooltipContent>
        )}
      </Tooltip>

      {submenu && showSubmenu && isHovered && <div className="absolute left-full top-0 w-6 h-[50px] z-40" />}

      {/* Submenu Panel - Improved styling */}
      {submenu && showSubmenu && isHovered && (
        <div className="absolute left-full top-0 ml-[4px] z-50 animate-in fade-in-0 slide-in-from-left-1 duration-150">
          <div
            className="bg-black/95 backdrop-blur-sm rounded-lg shadow-2xl p-1.5 min-w-[180px] border border-white/10 text-white"
            style={{
              background: `
                radial-gradient(circle at 20% 20%, rgba(95, 213, 222, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(192, 222, 96, 0.15) 0%, transparent 50%),
                rgba(0, 0, 0, 0.95)
              `
            }}>
            {/* Optional header - you can remove this if you prefer */}
            {/* <div className="px-3 py-1.5 text-[10px] font-semibold text-white/40 uppercase tracking-wider">
              {title}
            </div> */}

            <div className="flex flex-col gap-1">
              {submenu.map(
                (item) =>
                  item.show && (
                    <Link key={item.link} to={item.link} onClick={() => setIsHovered(false)}>
                      <div
                        className={`flex items-center gap-2.5 px-4 py-2.5 rounded-full transition-all text-sm ${
                          location.pathname === item.link
                            ? 'bg-gradient-to-r from-[#5FD5DE]/20 to-[#C0DE60]/20 text-white font-medium'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }`}>
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
